<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, date, string } from 'yup'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import {
  DCard,
  DBtn,
  DTextField,
  DSelectFieldSearch,
  DMultiselect,
  DModal,
  DLoading,
  DEmployeesFilter,
  FilterLinesIcon,
  Users01Icon,
  DToggleField
} from 'front-library'
import {
  convertArrayToString,
  ReportNet,
  generateNet,
  getPayroll,
  getProcessCountry,
  getEmployeesReport,
  getBanks,
  getConcepts,
  getCurrencies,
  getPaymentTypes,
  getPaymentBasesWithoutCountry,
  getReportRequest,
  getProcessedPayrolls,
  filterProcessSelectReports
} from '@/app/core/services/reports'
usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos */
const itemsPayrolls = ref<any[]>([])
const itemsBanks = ref<any[]>([])
const itemsConcepts = ref<any[]>([])
const allConcepts = ref<any[]>([])
const allIdsConceptsSelected = ref<any[]>([])
const itemsCurrencies = ref<any[]>([])
const itemsPaymentTypes = ref<any[]>([])
const itemsPaymentBases = ref<any[]>([])
const itemsEmployees = ref<string[]>([])
const itemsProcesses = ref<string[]>([])
const itemsStatusPayroll = ref([
  { id: 'PRENOMINA', name: 'PRENOMINA' },
  { id: 'CLOSED', name: 'NOMINA' }
])
const itemsOrder = ref([
  { id: 'identification', name: 'Cédula' },
  { id: 'consecutive', name: 'Consecutivo' }
])
const allPaymentBases = ref<any[]>([])
const allIdsPaymentBasesSelected = ref<any[]>([])
const searchStatus = ref<string>('')
const searchProcess = ref<string>('')
const urlFile = ref(null)
const refresh = ref()

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  process: string().required(t('form.valid.required')),
  startDate: date().required(t('form.valid.required')),
  endDate: date().when('startDate', (startDate: any, schema) => {
    if (startDate[0] !== undefined) {
      return schema
        .min(
          startDate,
          `${t('form.valid.startDateMin')} ${dayjs(startDate).format(
            'DD/MM/YYYY'
          )}`
        )
        .required(t('form.valid.required'))
    }
    return schema.required(t('form.valid.required'))
  }),
  status: string().required(t('form.valid.required')),
  concepts: array()
    .min(1, t('form.valid.minArray')),
  banks: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  currencies: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  salaryPaymentTypes: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  paymentBases: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  paymentDate: string().required(t('form.valid.required')),
  employees: array()
    .min(1, t('form.valid.employeeSelectionRequired'))
    .required(t('form.valid.fieldRequired', { field: t('fields.employees') }))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any>({
  initialValues: {
    translate: 'es',
    showCodes: false,
    showSucursals: false,
    showExchangeRates: false
  },
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: process } = useField<string>(ref('process'))
const { value: concepts } = useField<string[]>(ref('concepts'))
const { value: startDate } = useField<string | undefined>('startDate')
const { value: endDate } = useField<string | undefined>('endDate')
const { value: status } = useField<string>(ref('status'))
const { value: banks } = useField<string[]>(ref('banks'))
const { value: currencies } = useField<string[]>(ref('currencies'))
const { value: salaryPaymentTypes } = useField<string[]>(
  ref('salaryPaymentTypes')
)
const { value: paymentBases } = useField<string[]>(ref('paymentBases'))
const { value: paymentDate } = useField<string>(ref('paymentDate'))
const { value: showCodes } = useField<boolean>(ref('showCodes'))
const { value: showSucursals } = useField<boolean>(ref('showSucursals'))
const { value: showExchangeRates } = useField<boolean>(ref('showExchangeRates'))
const { value: orderBy } = useField<string>(ref('orderBy'))
const { value: employees } = useField<any[]>(ref('employees'))
const { value: format } = useField(ref('format'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  values.concepts = allIdsConceptsSelected.value
  validateData(values)
  const path = route.params.code as string
  const decodeCode = atob(path)
  const createReport: ReportNet = {
    code: decodeCode,
    parameters: values
  }
  void generateReport(createReport)
})

/* Validar datos */
const validateData = (data: any) => {
  itemsCurrencies.value?.length === data.currencies.length
    ? (data.currencies = [])
    : data.currencies
  itemsPaymentTypes.value?.length === data.salaryPaymentTypes.length
    ? (data.salaryPaymentTypes = [])
    : data.salaryPaymentTypes
  itemsPaymentBases.value?.length === data.paymentBases.length
    ? (data.paymentBases = [])
    : (data.paymentBases = allIdsPaymentBasesSelected.value)
  itemsBanks.value?.length === data.banks.length
    ? (data.banks = [])
    : data.banks
}

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateNet(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado del la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null
  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

/* Listar nóminas procesadas */
const getListProcessedPayroll = async () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
  let _dateIni = startDate.value
  let _dateEnd = endDate.value
  let _employees: any = []
  itemsEmployees.value = []
  employees.value = []
  try {
    if (
      process.value != undefined &&
      _dateIni != undefined &&
      _dateEnd != undefined &&
      status.value &&
      payrolls.value?.length > 0
    ) {
      loading.value = true
      messageLoading.value = t('paymentReceipts.loading.payroll')
      let strPayrolls = await convertArrayToString(payrolls.value)
      let _ProcessedPayroll = await getProcessedPayrolls(
        strPayrolls,
        `'${process.value}'`,
        _dateIni,
        _dateEnd,
        status.value
      )
      loading.value = false
      _employees = validateDataProcessedPayroll(_ProcessedPayroll)
      const strEmployees = await convertArrayToString(_employees)
      getEmployees(strEmployees)
    }
  } catch (error: any) {
    showError(error)
  }
}

/* validar datos de nóminas procesadas */
const validateDataProcessedPayroll = (data: any) => {
  const dataFilters = {
    processedPayroll: data,
    payrolls: payrolls.value,
    process: process.value,
    status: status.value
  }
  return filterProcessSelectReports(dataFilters)
}

/* Listar empleados */
const getEmployees = async (employeesIds: string) => {
  loading.value = true
  messageLoading.value = t('loading.employee')
  try {
    if (employeesIds !== '') {
      itemsEmployees.value = await getEmployeesReport('', '', employeesIds)
      employees.value = itemsEmployees.value.map((_: any) => _.id)
    } else {
      notifications.addNotification({
        title: t('reports.notification.noEmployeesFound'),
        text: t('reports.notification.noEmployees'),
        type: 'warning',
        time: 10000
      })
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Listar conceptos */
const listConcepts = async () => {
  loading.value = true
  messageLoading.value = t('loading.concepts')
  itemsConcepts.value = []
  try {
    if (payrolls.value?.length > 0 && process.value != undefined) {
      const strPayroll = await convertArrayToString(payrolls.value)
      allConcepts.value = await getConcepts(
        strPayroll,
        'nets',
        '',
        `'${process.value}'`
      )

      allConcepts.value.forEach((_: any) => {
        if (_.master_code.includes('999999')) {
          concepts.value = [_.code]
        }
      })

      if (concepts.value?.length > 0) filterCodeConcepts()

      itemsConcepts.value = [
        ...new Map(
          allConcepts.value.map((item: any) => [item['code'], item])
        ).values()
      ]

      if (itemsConcepts.value.length === 0) {
        notifications.addNotification({
          title: t('reports.notification.noConceptsFound'),
          text: t('reports.notification.noConcepts'),
          type: 'warning',
          time: 10000
        })
      }
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Filtrar codigos de conceptos */
const filterCodeConcepts = async () => {
  console.log(concepts.value)
  allIdsConceptsSelected.value = allConcepts.value
  allIdsConceptsSelected.value = allIdsConceptsSelected.value
    .filter(item => concepts.value.indexOf(item.code) > -1)
    .map(_ => _.id)

  console.log(allIdsConceptsSelected.value)
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

const filterPaymentBasesCode = (codes: any) => {
  allIdsPaymentBasesSelected.value = allPaymentBases.value
  allIdsPaymentBasesSelected.value = allIdsPaymentBasesSelected.value
    .filter((item: any) => codes.indexOf(item.code) > -1)
    .map((_: any) => _.id)
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const [
      promisePayrolls,
      promiseProcess,
      promiseBanks,
      promiseCurrencies,
      promisePaymentTypes,
      promisePaymentBases
    ] = await Promise.all([
      getPayroll(store.user?.id as string),
      getProcessCountry(),
      getBanks(),
      getCurrencies(),
      getPaymentTypes(),
      getPaymentBasesWithoutCountry()
    ])
    itemsPayrolls.value = promisePayrolls
    payrolls.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)
    itemsProcesses.value = promiseProcess
    itemsBanks.value = promiseBanks
    banks.value = itemsBanks.value.map((_: any) => _.id)
    itemsCurrencies.value = promiseCurrencies
    currencies.value = itemsCurrencies.value.map((_: any) => _.id)
    itemsPaymentTypes.value = promisePaymentTypes
    salaryPaymentTypes.value = itemsPaymentTypes.value.map((_: any) => _.id)
    allPaymentBases.value = promisePaymentBases

    itemsPaymentBases.value = [
      ...new Map(
        allPaymentBases.value.map((item: any) => [item['code'], item])
      ).values()
    ]

    paymentBases.value = itemsPaymentBases.value.map((_: any) => _.code)
    filterPaymentBasesCode(paymentBases.value)
    orderBy.value = 'identification'
    paymentDate.value = dayjs().format('YYYY-MM-DD')
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.net')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 lg:col-span-3 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :headerTitle="t('reports.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.payrolls"
                    @change="listConcepts(), getListProcessedPayroll()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.process')"
                    v-model="process"
                    :required="true"
                    :options="filterOptions(itemsProcesses, searchProcess)"
                    :error="errors.process"
                    @filter="searchProcess = $event"
                    @change="listConcepts(), getListProcessedPayroll" />
                </div>
                <!-- <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.concepts')"
                    v-model="concepts"
                    value-key="code"
                    :headerTitle="t('reports.fields.concepts')"
                    :required="false"
                    :options="itemsConcepts"
                    :error="errors.concepts"
                    @change="filterCodeConcepts()" />
                </div> -->
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.startDate')"
                    v-model="startDate"
                    :required="true"
                    :error="errors.startDate"
                    @change="getListProcessedPayroll" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.endDate')"
                    v-model="endDate"
                    :required="true"
                    :error="errors.endDate"
                    @change="getListProcessedPayroll" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.calculationStatus')"
                    v-model="status"
                    :options="filterOptions(itemsStatusPayroll, searchStatus)"
                    :required="true"
                    :error="errors.status"
                    @filter="searchStatus = $event"
                    @change="getListProcessedPayroll" />
                </div>
                <hr class="col-span-6" />
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.banks')"
                    :required="true"
                    :header-title="t('reports.fields.banks')"
                    v-model="banks"
                    :options="itemsBanks"
                    :error="errors.banks" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.coinsAccount')"
                    v-model="currencies"
                    :header-title="t('reports.fields.coinsAccount')"
                    :required="true"
                    :options="itemsCurrencies"
                    :error="errors.currencies" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.salaryPaymentTypes')"
                    :required="true"
                    :header-title="t('reports.fields.salaryPaymentTypes')"
                    v-model="salaryPaymentTypes"
                    :options="itemsPaymentTypes"
                    :error="errors.salaryPaymentTypes" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.paymentBases')"
                    :required="true"
                    :header-title="t('reports.fields.paymentBases')"
                    v-model="paymentBases"
                    value-key="code"
                    :options="itemsPaymentBases"
                    :error="errors.paymentBases"
                    @change="filterPaymentBasesCode(paymentBases)" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.advancementDate')"
                    v-model="paymentDate"
                    :required="true"
                    :error="errors.paymentDate" />
                </div>
                <hr class="col-span-6" />
                <h1
                  class="col-span-6 text-gray-600"
                  v-html="t('reports.tabs.modifiers')" />
                <div
                  class="col-span-6 sm:col-span-3 lg:col-span-2 flex place-items-center">
                  <DToggleField
                    :label="t('reports.fields.showCodes')"
                    v-model="showCodes" />
                </div>
                <div
                  class="col-span-6 sm:col-span-3 lg:col-span-2 flex place-items-center">
                  <DToggleField
                    :label="t('reports.fields.showBranches')"
                    v-model="showSucursals" />
                </div>
                <div
                  class="col-span-6 sm:col-span-3 lg:col-span-2 flex place-items-center">
                  <DToggleField
                    :label="t('reports.fields.showExchangeRate')"
                    v-model="showExchangeRates" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.orderBy')"
                    v-model="orderBy"
                    :options="itemsOrder" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="pl-4 pr-4 lg:pr-0 flex justify-between">
                <h3
                  class="text-md font-medium leading-6 text-gray-900"
                  v-html="t('fields.employees')" />
                <h3
                  class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter
                    :items="itemsEmployees"
                    v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div
                class="flex justify-center items-center p-5 sm:gap-4 lg:gap-3 xl:gap-4">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{
                    `${
                      employees?.length === undefined ? 0 : employees?.length
                    }/${itemsEmployees?.length}`
                  }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                color="excel"
                class="px-5 py-2 font-semibold"
                @click.prevent="
                  onSubmit(),
                    (format =
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
                "
                v-html="t('form.button.excel')" />
              <DBtn
                color="danger"
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit(), (format = 'application/pdf')"
                v-html="t('form.button.pdf')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
